import axios from 'axios';

import {
  IBrandingEditorLockAttempt,
  IBrandingEditorLockState,
} from '../interfaces/brandingEditorLock';
import { resolveAuthorizationHeader } from '../http/axios';
import { env } from '../env';

const { REACT_APP_API_URL } = env;

/**
 * Reads a `409 Conflict` body as a lock state, when the backend shaped it as one.
 *
 * Both the acquire and the heartbeat routes answer 409 with the current state
 * rather than an error body, so the caller learns who holds the lock from the
 * refusal itself.
 *
 * @param error - Whatever axios threw.
 * @returns The state carried by the refusal, or `null` for any other failure.
 */
const readConflictState = (error: unknown): IBrandingEditorLockState | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }

  const data = error.response.data as
    | Partial<IBrandingEditorLockState>
    | undefined;

  return data && typeof data.eventId === 'string'
    ? (data as IBrandingEditorLockState)
    : null;
};

/**
 * REST client for the experience's exclusive Editor Mode lock
 * (`api/v2/events/{eventId}/branding-editor-lock`).
 */
export default class BrandingEditorLockService {
  /**
   * @param eventId - Experience whose lock is addressed.
   * @returns The route, without a trailing segment.
   */
  private static lockUrl(eventId: string): string {
    return `${REACT_APP_API_URL}api/v2/events/${encodeURIComponent(
      eventId,
    )}/branding-editor-lock`;
  }

  /**
   * Reads who is editing the experience right now.
   *
   * @param eventId - Experience to read.
   * @returns The evaluated lock state; a stale lock already reads as `null`.
   */
  static getLock(eventId: string): Promise<IBrandingEditorLockState> {
    return axios
      .get(BrandingEditorLockService.lockUrl(eventId))
      .then((res) => res.data);
  }

  /**
   * Takes the lock for this hub session.
   *
   * @param eventId - Experience to lock.
   * @param sessionId - This hub's WebSocket session id.
   * @returns `held: false` with the blocking holder's state on a 409; rethrows anything else.
   */
  static async acquireLock(
    eventId: string,
    sessionId: string,
  ): Promise<IBrandingEditorLockAttempt> {
    try {
      const res = await axios.post(BrandingEditorLockService.lockUrl(eventId), {
        sessionId,
      });

      return { held: true, state: res.data };
    } catch (error) {
      const state = readConflictState(error);

      if (state) {
        return { held: false, state };
      }

      throw error;
    }
  }

  /**
   * Keeps this session's lock alive.
   *
   * @param eventId - Experience whose lock to refresh.
   * @param sessionId - This hub's WebSocket session id.
   * @returns `held: false` with the current state when the lock is no longer this session's.
   */
  static async heartbeat(
    eventId: string,
    sessionId: string,
  ): Promise<IBrandingEditorLockAttempt> {
    try {
      const res = await axios.put(
        `${BrandingEditorLockService.lockUrl(eventId)}/heartbeat`,
        { sessionId },
      );

      return { held: true, state: res.data };
    } catch (error) {
      const state = readConflictState(error);

      if (state) {
        return { held: false, state };
      }

      throw error;
    }
  }

  /**
   * Lets this session's lock go. Idempotent on the backend.
   *
   * With `keepalive` the request goes through `fetch` so it survives the page
   * being unloaded — axios cannot do that — and its answer is not awaited.
   *
   * @param eventId - Experience whose lock to release.
   * @param sessionId - This hub's WebSocket session id.
   * @param options.keepalive - Fire-and-forget variant for `pagehide`.
   * @returns The state after the call, or `null` on the keepalive path.
   */
  static async releaseLock(
    eventId: string,
    sessionId: string,
    options: { keepalive?: boolean } = {},
  ): Promise<IBrandingEditorLockState | null> {
    const url = `${BrandingEditorLockService.lockUrl(
      eventId,
    )}?sessionId=${encodeURIComponent(sessionId)}`;

    if (options.keepalive) {
      const authorization = await resolveAuthorizationHeader();

      void fetch(url, {
        method: 'DELETE',
        headers: authorization ? { Authorization: authorization } : {},
        keepalive: true,
      }).catch((error) => {
        console.warn('Keepalive branding editor lock release failed', {
          eventId,
          error,
        });
      });

      return null;
    }

    return axios.delete(url).then((res) => res.data);
  }
}
